import { Helmet } from 'react-helmet-async';
import { NavBar } from '@/components/navbar';
import { Footer } from '@/components/SiteFooter';
import { FileText, ShieldCheck, Zap, Search, Sparkles, BookOpen, Lightbulb, Bell, FileCheck } from 'lucide-react';
import ToolCard from '@/components/tools/ToolCard';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';

// Define tool categories
const categories = [
  { id: 'all', name: 'All' },
  { id: 'compliance', name: 'Compliance' },
  { id: 'optimization', name: 'Optimization' },
  { id: 'analysis', name: 'Analysis' },
];

const Tools = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Scroll-based animations
  const { scrollYProgress } = useScroll({
    target: pageRef,
    offset: ['start start', 'end end'],
  });

  const headerOpacity = useTransform(scrollYProgress, [0, 0.1], [1, 0.9]);

  // Define tools with categories
  const tools = [
    {
      icon: Zap,
      title: 'RegTech AI',
      description:
        'AI process optimizer for compliance managers to identify manual processes that can be improved',
      benefits: [
        'Identify manual processes related to global regulations',
        'Calculate potential time and cost savings from AI optimization',
        'Get actionable recommendations for process improvements',
        'Reduce compliance workload with targeted automation',
        'Increase efficiency while maintaining regulatory adherence',
      ],
      path: '/regtechai',
      color: 'purple',
      status: 'active' as const,
      category: 'optimization',
    },
    {
      icon: ShieldCheck,
      title: 'Compliance AI',
      description: 'AI-powered compliance assessment and recommendations for regulatory frameworks',
      benefits: [
        'Quickly analyze your compliance status against major regulations',
        'Receive actionable recommendations to address compliance gaps',
        'Save time and resources on manual compliance reviews',
        'Reduce the risk of non-compliance penalties',
        'Stay updated with changing regulatory requirements',
      ],
      path: '/compliance-ai',
      color: 'purple',
      status: 'active' as const,
      category: 'compliance',
    },
    {
      icon: FileText,
      title: 'Compliance Calendar',
      description: 'AI-generated regulatory timeline for EU directives and requirements',
      benefits: [
        "Get customized compliance calendars based on your organization's profile",
        'Stay ahead of upcoming EU regulatory deadlines',
        'Understand which regulations apply to your specific situation',
        'Receive prioritized action items for compliance activities',
        'Plan resources effectively with advanced visibility',
      ],
      path: '/compliance-calendar',
      color: 'purple',
      status: 'active' as const,
      category: 'compliance',
    },
    {
      icon: Bell,
      title: 'Alert Center',
      description: 'Get notifications about regulatory changes that affect your business',
      benefits: [],
      path: '#',
      color: 'blue',
      status: 'coming-soon' as const,
      category: 'compliance',
    },
    {
      icon: Sparkles,
      title: 'AI Document Scanner',
      description: 'Scan and analyze documents for compliance-related information',
      benefits: [],
      path: '#',
      color: 'amber',
      status: 'coming-soon' as const,
      category: 'analysis',
    },
    {
      icon: FileCheck,
      title: 'Compliance Report Builder',
      description: 'Generate professional compliance reports for stakeholders',
      benefits: [],
      path: '#',
      color: 'emerald',
      status: 'beta' as const,
      category: 'compliance',
    },
  ];

  // Filter tools based on active category and search query
  const filteredTools = tools.filter(tool => {
    const matchesCategory = activeCategory === 'all' || tool.category === activeCategory;
    const matchesSearch = tool.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         tool.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div ref={pageRef} className="relative min-h-screen">
      <Helmet>
        <title>Verdea | Compliance Tools Suite</title>
        <meta
          name="description"
          content="Access powerful AI-powered tools to streamline compliance processes, reduce workload, and ensure regulatory adherence."
        />
      </Helmet>

      <NavBar />

      {/* Background Elements - Apple style with frosted glass look */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        {/* Light blur gradient background */}
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e7eb 100%)',
          }}
        />
        
        {/* Primary gradient accent */}
        <div
          className="absolute top-0 right-0 w-[40vw] h-[40vh] blur-3xl opacity-30"
          style={{
            background: 'radial-gradient(circle at center, rgba(124, 58, 237, 0.3) 0%, rgba(124, 58, 237, 0) 70%)',
            transform: 'translate(20%, -20%)',
          }}
        />
      </div>

      <main className="relative pt-20 pb-16 min-h-screen overflow-hidden">
        <div className="container mx-auto px-4 md:px-6 relative z-10">

          <motion.div
              style={{ opacity: headerOpacity }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center "
            >
              <span className="inline-block px-4 py-1.5 mb-6 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-sm font-medium dark:bg-blue-900/20 dark:border-blue-800/30 dark:text-blue-400">
                Free Compliance Tools
              </span>
              <h1 className="text-4xl md:text-6xl font-medium tracking-tight text-gray-900 dark:text-gray-100 mb-6 leading-tight">
                AI-Powered
                <br />
                <span className="bg-gradient-to-r from-purple-600 to-indigo-500 bg-clip-text text-transparent">
                  Compliance Tools
                </span>
              </h1>

              <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400 mb-8 max-w-3xl mx-auto leading-relaxed">
                Powerful AI-powered tools to streamline your compliance processes.
              </p>
            </motion.div>





          {/* Search and Filter Section - Apple Style */}
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-800">
              {/* Search Bar */}
              <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search tools..."
                  className="pl-10 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 rounded-lg"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              {/* Category Tabs - Apple Style */}
              <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-all
                      ${activeCategory === category.id
                        ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                      }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Tools List - Apple App Store Style */}
          <div className="max-w-4xl mx-auto mb-10">
            <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {filteredTools.length > 0 ? (
                  filteredTools.map((tool, index) => (
                    <ToolCard
                      key={index}
                      icon={tool.icon}
                      title={tool.title}
                      description={tool.description}
                      benefits={tool.benefits}
                      path={tool.path}
                      color={tool.color}
                      status={tool.status}
                    />
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <BookOpen className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No tools found matching your criteria.</p>
                    <p className="text-sm mt-1">Try changing your search or category filter.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Request New Tools Section */}
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/10 dark:to-indigo-900/10 rounded-xl p-6 border border-purple-100 dark:border-purple-800/20">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-800/20 text-purple-600 dark:text-purple-400">
                    <Lightbulb className="h-5 w-5" />
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    Don't see the tool you need? We're constantly expanding our toolkit.
                  </p>
                </div>
                <Link to="/contact">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-purple-200 dark:border-purple-800/30 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg whitespace-nowrap"
                  >
                    Request a Tool
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Tools;